export type ProjectionLog = {
    id?: number | string;
    type: string;
    createdAt: number;
    executor?: string | null;
    target?: string | null;
    channel?: string | null;
    from?: string | null;
    to?: string | null;
    role?: string | null;
    extra?: Record<string, unknown>;
};

export type ProjectionEntity = {
    id: string;
};

export type ProjectionQuery = {
    name?: string;
    expression: string;
};

export type ProjectionWindow = {
    from?: number;
    after?: number;
    to?: number;
    before?: number;
    at?: number;
};

export type ProjectionQueryResult<TLog extends ProjectionLog = ProjectionLog> = {
    logs: TLog[];
    members?: ProjectionEntity[];
    channels?: ProjectionEntity[];
    partial?: boolean;
};

export type ProjectionQueryProvider<TLog extends ProjectionLog = ProjectionLog> = {
    fetchAll(options: {
        query: Required<ProjectionQuery>;
        window: ProjectionWindow;
        signal?: AbortSignal;
    }): Promise<ProjectionQueryResult<TLog>>;
};

export type ProjectionApplyMeta<TContext> = {
    source: string;
    context: TContext;
};

export type ProjectionFinalizeMeta = {
    partial: boolean;
    errors: Array<{
        source: string;
        error: unknown;
    }>;
    eventCount: number;
    members: ProjectionEntity[];
    channels: ProjectionEntity[];
};

export type ProjectionDefinition<TState, TContext, TResult, TLog extends ProjectionLog = ProjectionLog> = {
    queries(context: TContext): Array<string | ProjectionQuery>;
    createState(context: TContext): TState;
    apply(state: TState, log: TLog, meta: ProjectionApplyMeta<TContext>): void;
    finalize(state: TState, context: TContext, meta: ProjectionFinalizeMeta): TResult;
};

export type ProjectionRunOptions<TState, TContext, TResult, TLog extends ProjectionLog = ProjectionLog> = {
    projection: ProjectionDefinition<TState, TContext, TResult, TLog>;
    context: TContext;
    window: ProjectionWindow;
    signal?: AbortSignal;
};

export type ProjectionRunResult<TState, TResult, TLog extends ProjectionLog = ProjectionLog> = ProjectionFinalizeMeta & {
    result: TResult;
    state: TState;
    events: TLog[];
};

function createAbortError(): DOMException {
    return new DOMException("The operation was aborted.", "AbortError");
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted)
        throw createAbortError();
}

function normalizeQuery(query: string | ProjectionQuery, index: number): Required<ProjectionQuery> {
    if (typeof query === "string")
        return {
            name        : `query_${index}`,
            expression  : query,
        };

    return {
        name        : query.name ?? `query_${index}`,
        expression  : query.expression,
    };
}

function createEventKey(event: ProjectionLog, source: string, index: number): string {
    return [
        event.id,
        event.type,
        event.createdAt,
        event.executor,
        event.target,
        event.channel,
        event.from,
        event.to,
        source,
        index,
    ].filter((value) => value !== null && value !== undefined).join(":");
}

function mergeEntities(entities: ProjectionEntity[]): ProjectionEntity[] {
    const byId = new Map<string, ProjectionEntity>();

    for (const entity of entities)
        if (entity?.id)
            byId.set(entity.id, entity);

    return [ ...byId.values() ];
}

function mergeEvents<TLog extends ProjectionLog>(results: Array<ProjectionQueryResult<TLog> & { source: string }>) {
    const byKey = new Map<string, {
        event: TLog;
        source: string;
        index: number;
    }>();
    let index = 0;

    for (const result of results) {
        for (const event of result.logs) {
            byKey.set(createEventKey(event, result.source, index), {
                event,
                source  : result.source,
                index,
            });
            index++;
        }
    }

    return [ ...byKey.values() ].sort((left, right) => {
        const createdAtDiff = left.event.createdAt - right.event.createdAt;

        if (createdAtDiff !== 0)
            return createdAtDiff;

        return left.index - right.index;
    });
}

export class ProjectionRunner<TLog extends ProjectionLog = ProjectionLog> {
    readonly queryProvider: ProjectionQueryProvider<TLog>;

    constructor({ queryProvider }: { queryProvider: ProjectionQueryProvider<TLog> }) {
        this.queryProvider = queryProvider;
    }

    async run<TState, TContext, TResult>({
        projection,
        context,
        window,
        signal,
    }: ProjectionRunOptions<TState, TContext, TResult, TLog>): Promise<ProjectionRunResult<TState, TResult, TLog>> {
        throwIfAborted(signal);

        const state = projection.createState(context);
        const queries = (projection.queries(context) ?? []).map(normalizeQuery);
        const results: Array<ProjectionQueryResult<TLog> & { source: string }> = [];
        const errors: ProjectionFinalizeMeta["errors"] = [];

        for (const query of queries) {
            throwIfAborted(signal);

            try {
                const result = await this.queryProvider.fetchAll({
                    query,
                    window,
                    signal,
                });

                results.push({
                    ...result,
                    source: query.name,
                });

            } catch (error) {
                if (error instanceof DOMException && error.name === "AbortError")
                    throw error;

                errors.push({
                    source  : query.name,
                    error,
                });
            }
        }

        const entries = mergeEvents(results);

        for (const entry of entries) {
            throwIfAborted(signal);
            projection.apply(state, entry.event, {
                source  : entry.source,
                context,
            });
        }

        const meta: ProjectionFinalizeMeta = {
            partial     : errors.length > 0 || results.some((result) => result.partial),
            errors,
            eventCount  : entries.length,
            members     : mergeEntities(results.flatMap((result) => result.members ?? [])),
            channels    : mergeEntities(results.flatMap((result) => result.channels ?? [])),
        };

        return {
            result: projection.finalize(state, context, meta),
            state,
            events: entries.map((entry) => entry.event),
            ...meta,
        };
    }
}
